import { cx } from "@emotion/css";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormik } from "formik";
import { FilePen, Pencil, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { FormTextField } from "src/components/Default/LekUI/TextField";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { useFontSize } from "src/hooks/useFontSize";
import { ModalMaxWidth } from "src/types/enums";
import * as Yup from "yup";

export interface ChangeNameModalProps {
	onClose: () => void;
	name: string;
	/** Akcja zapisu. Modal pokazuje loading i zamyka się po sukcesie. */
	onSave: (name: string) => Promise<unknown>;
	title: string;
	label: string;
	placeholder?: string;
}

export const ChangeNameModal = ({
	onClose,
	name,
	onSave,
	title,
	label,
	placeholder,
}: ChangeNameModalProps) => {
	const { t } = useTranslation();
	const { modalTextFontSizeClass, fontSizeClass } = useFontSize();
	const { handleSubmit, setFieldValue, values, errors, isSubmitting } =
		useFormik({
			initialValues: { name },
			validateOnChange: true,
			onSubmit: async (values) => {
				await onSave(values.name.trim());
				onClose();
			},
			validationSchema: Yup.object().shape({
				name: Yup.string()
					.trim()
					.required(t("modals.changeName.empty", "Nazwa nie może być pusta.")),
			}),
		});

	const isChanged = !errors.name && values.name !== name;

	return (
		<Modal onClose={onClose} maxWidth={ModalMaxWidth.sm}>
			<form
				className="modal-layout"
				data-test-id="change-name-modal"
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<ModalHeader Icon={FilePen} title={title} hasCloseButton />
				<div className="flex flex-col gap-8">
					<div className="flex items-center justify-between gap-12">
						<label
							htmlFor="change-name-input"
							className={cx(modalTextFontSizeClass, "text-dark-grey")}
						>
							{label}
						</label>
						{isChanged && (
							<span className="chip chip--primary flex items-center gap-4">
								<Pencil className="icon-super-small" />
								{t("modals.changeName.changed", "Zmieniono")}
							</span>
						)}
					</div>
					<FormTextField
						id="change-name-input"
						fullWidth
						autoFocus
						placeholder={placeholder}
						value={values.name}
						error={errors.name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setFieldValue("name", e.target.value)
						}
						slotProps={{
							htmlInput: { "data-test-id": "change-name-input" },
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<Pencil className="icon-small icon-black-80" />
									</InputAdornment>
								),
							},
						}}
					/>
					{isChanged && (
						<div className="flex items-center gap-12">
							<span
								className={cx(
									fontSizeClass,
									"text-grey text-ellipsis-1 flex-1 min-w-0",
								)}
							>
								{t("modals.changeName.previous", "Poprzednio:")}{" "}
								<span className="text-line-through">{name}</span>
							</span>
							<TextPrimaryButton
								className="flex-shrink-0 gap-4"
								onClick={() => setFieldValue("name", name)}
								data-test-id="change-name-restore-button"
							>
								<RotateCcw className="icon-super-small" />
								{t("modals.changeName.restore", "Przywróć")}
							</TextPrimaryButton>
						</div>
					)}
				</div>
				<ModalFooter className="modal-layout--footer">
					<TextPrimaryButton
						className="btn-size-big border-radius-12"
						onClick={onClose}
						data-test-id="change-name-cancel-button"
					>
						{t("commands.cancel")}
					</TextPrimaryButton>
					<PrimaryButton
						type="submit"
						className="btn-size-big border-radius-12"
						isLoading={isSubmitting}
						disabled={!!errors.name}
						data-test-id="change-name-save-button"
					>
						{t("commands.save")}
					</PrimaryButton>
				</ModalFooter>
			</form>
		</Modal>
	);
};
